/**
 * Simple i18n system for HexaForms Widget
 * Supports English (en) and Spanish (es)
 * Designed to be lightweight and extensible for future languages
 */

export type Language = 'es' | 'en';

interface Translations {
  es: TranslationSet;
  en: TranslationSet;
}

interface TranslationSet {
  submit: string;
  submitting: string;
  success: {
    title: string;
    message: string;
  };
  error: {
    submit: string;
    load: string;
    backend: string;
  };
  draft: {
    found: string;
    recover: string;
    discard: string;
  };
  validation: {
    required: string;
    email: string;
    phone: string;
    number: string;
    url: string;
    minLength: string;
    maxLength: string;
  };
  file: {
    tooLarge: string;
    invalidType: string;
  };
  upload: {
    placeholder: string;
  };
  select: {
    placeholder: string;
  };
  navigation: {
    next: string;
    previous: string;
  };
}

const translations: Translations = {
  es: {
    submit: 'Enviar',
    submitting: 'Enviando...',
    success: {
      title: '¡Gracias!',
      message: 'Tu respuesta ha sido enviada exitosamente.',
    },
    error: {
      submit: 'Hubo un error al enviar el formulario. Por favor, intenta de nuevo.',
      load: 'Error al cargar el formulario',
      backend: 'El backend respondió con un error',
    },
    draft: {
      found: 'Encontramos un borrador guardado. ¿Quieres recuperarlo?',
      recover: 'Recuperar',
      discard: 'Descartar',
    },
    validation: {
      required: 'Este campo es obligatorio',
      email: 'Debe ser un email válido',
      phone: 'Debe ser un teléfono válido',
      number: 'Debe ser un número válido',
      url: 'Debe ser una URL válida',
      minLength: 'Debe tener al menos {min} caracteres',
      maxLength: 'No puede tener más de {max} caracteres',
    },
    file: {
      tooLarge: 'El archivo "{name}" es demasiado grande. Tamaño máximo: {max}MB',
      invalidType: 'Tipo de archivo no permitido para "{name}". Tipos permitidos: {types}',
    },
    upload: {
      placeholder: 'Arrastra un archivo aquí o haz clic para seleccionar',
    },
    select: {
      placeholder: 'Selecciona una opción',
    },
    navigation: {
      next: 'Siguiente',
      previous: 'Anterior',
    },
  },
  en: {
    submit: 'Submit',
    submitting: 'Submitting...',
    success: {
      title: 'Thank you!',
      message: 'Your response has been submitted successfully.',
    },
    error: {
      submit: 'There was an error submitting the form. Please try again.',
      load: 'Error loading form',
      backend: 'The backend responded with an error',
    },
    draft: {
      found: 'We found a saved draft. Would you like to recover it?',
      recover: 'Recover',
      discard: 'Discard',
    },
    validation: {
      required: 'This field is required',
      email: 'Must be a valid email',
      phone: 'Must be a valid phone number',
      number: 'Must be a valid number',
      url: 'Must be a valid URL',
      minLength: 'Must have at least {min} characters',
      maxLength: 'Cannot have more than {max} characters',
    },
    file: {
      tooLarge: 'File "{name}" is too large. Maximum size: {max}MB',
      invalidType: 'File type not allowed for "{name}". Allowed types: {types}',
    },
    upload: {
      placeholder: 'Drag a file here or click to select',
    },
    select: {
      placeholder: 'Select an option',
    },
    navigation: {
      next: 'Next',
      previous: 'Previous',
    },
  },
};

// Default language (will be overridden by detection)
let currentLanguage: Language = 'es';

/**
 * Set the current language
 */
export function setLanguage(lang: Language): void {
  if (lang === 'es' || lang === 'en') {
    currentLanguage = lang;
  } else {
    console.warn(`[HexaForms] Unsupported language: ${lang}. Falling back to Spanish.`);
    currentLanguage = 'es';
  }
}

/**
 * Get the current language
 */
export function getLanguage(): Language {
  return currentLanguage;
}

/**
 * Detect language from browser or return default
 */
export function detectLanguage(): Language {
  if (typeof window === 'undefined') {
    return 'es';
  }

  // Check for data attribute override
  const widgetElement = document.querySelector('[data-hexaforms-id]');
  if (widgetElement) {
    const langAttr = widgetElement.getAttribute('data-hexaforms-lang');
    if (langAttr === 'en' || langAttr === 'es') {
      return langAttr;
    }
  }

  // Detect from browser
  const browserLang = navigator.language.split('-')[0];
  return browserLang === 'en' ? 'en' : 'es';
}

/**
 * Translate a key with optional parameters
 * @param key - Translation key (e.g., 'success.title' or 'validation.required')
 * @param params - Parameters to replace in the translation (e.g., {label: 'Email', min: 5})
 */
export function t(key: string, params?: Record<string, string | number>): string {
  const keys = key.split('.');
  let value: any = translations[currentLanguage];

  // Navigate through nested keys
  for (const k of keys) {
    value = value?.[k];
  }

  // If translation not found, try Spanish as fallback
  if (typeof value !== 'string') {
    let fallbackValue: any = translations.es;
    for (const k of keys) {
      fallbackValue = fallbackValue?.[k];
    }
    value = fallbackValue;
  }

  // If still not found, return the key
  if (typeof value !== 'string') {
    console.warn(`[HexaForms] Translation missing: ${key} (language: ${currentLanguage})`);
    return key;
  }

  // Replace placeholders {param} with actual values
  if (params) {
    return value.replace(/\{(\w+)\}/g, (_, paramKey) => {
      const paramValue = params[paramKey];
      return paramValue !== undefined ? String(paramValue) : '';
    });
  }

  return value;
}

/**
 * Initialize i18n with language detection or explicit language
 */
export function initI18n(language?: Language): void {
  if (language) {
    setLanguage(language);
  } else {
    const detected = detectLanguage();
    setLanguage(detected);
  }
}
